import { useMemo } from 'react';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { getUnitSymbols } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';

export const getExportReportLocationRadiusTableColumns = (translate: TranslationFnc) => [{
  type: 'radius',
  id: '5',
  heading: '5mi',
  secondaryHeading: translate('Radius Circle'),
  unit: UnitSystem.imperial,
  distance: 5,
}, {
  type: 'radius',
  id: '10',
  heading: '10mi',
  secondaryHeading: translate('Radius Circle'),
  unit: UnitSystem.imperial,
  distance: 10,
}, {
  type: 'radius',
  id: '15',
  heading: '15mi',
  secondaryHeading: translate('Radius Circle'),
  unit: UnitSystem.imperial,
  distance: 15,
}] as const satisfies ReadonlyArray<ReportTableColumn>;

export const useExportReportParams = () => {
  const exportReportState = useToolsStateExportReportSelector();
  const [t] = useTranslation();

  const tableColumns = useMemo<ReadonlyArray<ReportTableColumn>>(() => {
    if (!exportReportState.isActive) {
      return [];
    }

    switch (exportReportState.params.type) {
      case ExportReportType.Location:
        return getExportReportLocationRadiusTableColumns(t);
      case ExportReportType.Radius: {
        const unit = getUnitSymbols(exportReportState.params.data.unit).unit;

        return [{
          type: 'radius',
          id: 'radius',
          heading: `${exportReportState.params.data.radius}${unit}`,
          secondaryHeading: t('Radius Circle'),
          unit: exportReportState.params.data.unit,
          distance: exportReportState.params.data.radius,
        }];
      }

      case ExportReportType.DriveTimePolygon: {
        return [{
          type: 'polygon',
          id: 'dtp',
          heading: `${exportReportState.params.data.minutes} min`,
          secondaryHeading: t('DT Polygon'),
        }];
      }

      case ExportReportType.Polygon: {
        return [{
          type: 'polygon',
          id: 'boundary',
          heading: exportReportState.params.data.heading,
          secondaryHeading: t('Boundary'),
        }];
      }

      default: {
        return [];
      }
    }
  }, [exportReportState, t]);

  return { tableColumns };
};
